import React, { useCallback, useRef, useEffect, useState } from "react";
import styles from "./PopupInfo.module.scss";
import { gsap } from "gsap";
import { useExperienceStore } from "../../store";
import { rooms } from "../../store/data/rooms";
import emitter from "../../emitter.js";
import Button from "../Shared/Button/Button";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';

function PopupInfo() {
  const wrapperRef = useRef();
  const setScenario = useExperienceStore((state) => state.setActiveScenario);
  const addRessource = useExperienceStore((state) => state.addRessource);
  const setCurrentRoom = useExperienceStore((state) => state.setCurrentRoom);
  const activeScenario = useExperienceStore((state) => state.activeScenario);
  const currentRoom = useExperienceStore((state) => state.currentRoom);
  const objectsInRoom = useExperienceStore((state) => state.objectsInRoom());
  const ressourcesSaved = useExperienceStore((state) => state.ressourcesSaved);
  const setRedirectObject = useExperienceStore(
    (state) => state.setRedirectObject,
  );
  const [isAnimating, setIsAnimating] = useState(false);
  const emitterRegistered = useRef(false);
  const completedScenarios = useExperienceStore((state) => state.completedScenarios)

  const [info, setInfo] = useState(null);

  useEffect(() => {
    // Bind the event listener
    function handleClickOutside(event) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        wrapperRef.current.style.opacity == 1
      ) {
        animateOut();
      }

    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  });

  useEffect(() => {
    gsap.set(wrapperRef.current, {
      y: 40,
    });

  }, [wrapperRef]);

  const animateOut = useCallback(() => {
    setIsAnimating(true);
    wrapperRef.current.setAttribute("aria-hidden", "true");
    return gsap
      .to(wrapperRef.current, {
        autoAlpha: 0,
        y: 40,
        ease: "expo.out",
        duration: 0.5,
      })
      .then(() => {
        setInfo(null);
        setIsAnimating(false);
        emitter.emit("setInfoState", false);
      });
  }, [setIsAnimating]);

  const animateIn = useCallback(() => {
    setIsAnimating(true);
    wrapperRef.current.setAttribute("aria-hidden", "false");
    // Définir le point d'origine de la transformation au coin inférieur droit
    gsap.set(wrapperRef.current, {
      transformOrigin: "bottom right",
    });

    return gsap.timeline({ onComplete: () => setIsAnimating(false) }).fromTo(
      wrapperRef.current,
      {
        autoAlpha: 0,
        scale: 1, // Commencez réduit à un point
        y: 100,
        x: 0,
      },
      {
        autoAlpha: 1,
        scale: 1, // Étendre à la taille réelle
        y: 0, // Déplacer à la position Y finale
        x: 0, // Déplacer à la position X finale
        ease: "expo.out",
        duration: 1,
      },
    );
  }, [setIsAnimating]);

  const saveRessource = useCallback(
    (ressource) => {
      addRessource(ressource);
      emitter.emit("cartClickEvent");
      emitter.emit("sound:addLink");
    },
    [addRessource],
  );

  const handleLinkClick = useCallback((url, label) => {
    // Pousser l'événement dans le Data Layer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'click.action',
      click: label,
      click_product: label,
      challenge_type: activeScenario ? activeScenario.id : null,
      page_chapter1: currentRoom || null,
    });
    // Ouvrir le lien dans un nouvel onglet
    window.open(url, '_blank');
  }, [activeScenario, currentRoom]);



  const redirectTo = useCallback((redirectObject) => {
    setRedirectObject(redirectObject);

    goToRoom(redirectObject.room);
  });

  const initScenario = useCallback(
    (id) => {
      if (activeScenario && activeScenario.id) {
        emitter.emit("clickScenarioEvent", activeScenario.id, "fin");
      }
      emitter.emit("clickScenarioEvent", id, "debut");
      setScenario(id);
      animateOut();
    },
    [setScenario, animateOut],
  );

  const goToRoom = useCallback(
    (id) => {
      emitter.emit("sound:transition", id);

      animateOut();
      if (info.scenario) {
        initScenario(info.scenario.id);
      }
      emitter.emit(
        "buttonClickEvent",
        "redirection",
        id,
        info.scenario ? info.scenario.id : null,
      );
      setCurrentRoom(id);
    },
    [info, initScenario, setCurrentRoom, animateOut],
  );

  const onPopupOpen = useCallback(
    (infoIn) => {
      animateOut().then(() => {
        setInfo(infoIn);
        emitter.emit("setInfoState", true);
        animateIn();


      });
    },
    [setInfo, animateIn, animateOut],
  );

  useEffect(() => {
    if (!emitterRegistered.current) {
      emitterRegistered.current = true;
      emitter.on("popup:open", onPopupOpen);
    }
  }, [onPopupOpen]);

  const handleClickRdv = () => {
    // Émettre un événement personnalisé
    emitter.emit('RDV', 'Click RDV');

    // Ouvrir le lien dans un nouvel onglet
    window.open("https://agence.macif.fr/assurance/#pf:CODE_TRACKING#", "_blank");
  };

  return (
    <>

      {info && (
        <div className={styles.overlay}></div>
      )}

      <div
        ref={wrapperRef}
        className={`${styles.popupInfo} ${info && (info.type === "finish" || (info && info.scenario && completedScenarios.includes(info.scenario.id))) ? styles.popupInfoFinish : ""
          } ${info && (info.type === "finish" || (info && info.scenario && completedScenarios.length === 5)) ? styles.popupInfoCompleted : ""
          }`}
        aria-hidden={true}
      >

        <button
          className={styles.popupClose}
          aria-label={"Bouton fermer la modale du contenu de context"}
          onClick={() => animateOut()}
        >
          <CloseIcon />
        </button>
        {info && info.type === "object" && (
          <>
            <h5 className={styles.popupInfoTitle} aria-live={"polite"}>
              {info.object.title}
            </h5>
            {info.object.content &&
              info.object.content.map((content, i) => (
                <React.Fragment key={`content-${i}`}>

                  {content.type === "text" && (
                    <>
                      <p
                        aria-live={"polite"}
                        key={`${content.type}-${i}`}
                        className={`${styles.popupInfoText} t3`}
                        dangerouslySetInnerHTML={{ __html: content.text }}
                      ></p>
                      {content.sourceTitle && (
                        <p className={`${styles.sourceText} t5`}>
                          {content.sourceUrl ? (
                            <a href={content.sourceUrl} target="_blank" rel="noreferrer">
                              {content.sourceTitle}
                            </a>
                          ) : (
                            content.sourceTitle
                          )}
                        </p>
                      )}
                    </>
                  )}
                  {content.type === "youtube" && (
                    <div className={styles.popupIframe} key={`youtube-${i}`}>
                      <iframe
                        key={`${content.type}-${i}`}
                        title="Video"
                        src={content.urlIframe}
                        allowFullScreen={true}
                      ></iframe>
                    </div>
                  )}
                  {content.type === "ressource" && (
                    <>
                      <div
                        key={`${content.type}-${i}`}
                        className={styles.popupRessource}
                      >
                        <p className={"t3"}>{content.label || content.text}</p>
                        <div className={styles.popupRessourceActions}>
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault(); // Empêcher le comportement par défaut du lien
                              handleLinkClick(content.url, content.label || content.text);
                            }}
                            aria-label={content.label}
                            rel="noreferrer"
                          >
                            <OpenInNewRoundedIcon />
                          </a>
                          <button onClick={() => saveRessource(content)}>
                            {ressourcesSaved &&
                              !ressourcesSaved.find(
                                (r) => r.url === content.url,
                              ) && (
                                <svg
                                  width="20"
                                  height="20"
                                  viewBox="0 0 16 13"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M15.6156 3.07078C15.2953 2.71646 14.8549 2.48024 14.3744 2.48024H8.20821C8.00801 2.48024 7.80781 2.63772 7.80781 2.87393C7.80781 3.11015 8.00801 3.26762 8.20821 3.26762H14.3744C14.6146 3.26762 14.8549 3.38573 15.015 3.5432C15.1752 3.74005 15.2553 3.97626 15.1752 4.21248L14.8148 6.18092H10.4104C10.2102 6.18092 10.01 6.3384 10.01 6.57461C10.01 6.81083 10.2102 6.9683 10.4104 6.9683H14.6947L14.3343 8.85801C14.2943 9.13359 14.014 9.33044 13.7337 9.33044H6.04605C5.76577 9.33044 5.52553 9.13359 5.44545 8.85801L3.88388 0.314952C3.84384 0.118107 3.68368 0 3.48348 0H0.4004C0.2002 0 0 0.157476 0 0.393689C0 0.629903 0.2002 0.787379 0.4004 0.787379H3.16316L4.68469 9.05486C4.76477 9.60602 5.16517 9.99971 5.68569 10.1572C5.48549 10.4328 5.32533 10.7871 5.32533 11.1808C5.32533 12.165 6.12613 12.9524 7.12713 12.9524C8.12813 12.9524 8.92893 12.165 8.92893 11.1808C8.92893 10.8265 8.80881 10.4721 8.60861 10.1966H11.0511C10.8509 10.4721 10.7307 10.8265 10.7307 11.1808C10.7307 12.165 11.5315 12.9524 12.5325 12.9524C13.5335 12.9524 14.3343 12.165 14.3343 11.1808C14.3343 10.7871 14.2142 10.4328 14.014 10.1572C14.5345 10.0391 14.975 9.60602 15.0951 9.05486L15.976 4.40932C16.0561 3.93689 15.9359 3.46447 15.6156 3.07078ZM8.12813 11.1414C8.12813 11.6926 7.68769 12.1256 7.12713 12.1256C6.56657 12.1256 6.12613 11.6926 6.12613 11.1414C6.12613 10.5902 6.56657 10.1572 7.12713 10.1572C7.68769 10.1572 8.12813 10.5902 8.12813 11.1414ZM12.5726 12.1256C12.012 12.1256 11.5716 11.6926 11.5716 11.1414C11.5716 10.5902 12.012 10.1572 12.5726 10.1572C13.1331 10.1572 13.5736 10.5902 13.5736 11.1414C13.5736 11.6926 13.1331 12.1256 12.5726 12.1256Z"
                                    fill="#0A2D82" />

                                  <defs>
                                    <clipPath id="clip0_739_2">
                                      <rect
                                        width="15.3061"
                                        height="12.9643"
                                        fill="white"
                                        transform="translate(0.102051 7.08936)"
                                      />
                                    </clipPath>
                                  </defs>
                                </svg>
                              )}
                          </button>
                        </div>
                      </div>
                      {content.cg && (
                        <>
                          <input
                            type="checkbox"
                            id="showCG"
                            name="showCG"
                            className={styles.showCG}
                          />
                          <label for="showCG" className={styles.showCGLabel}>
                            <span>{content.cglabel}</span>
                            <ExpandMoreRoundedIcon />
                          </label>
                          <p className={`${styles.popupCg} t5`}>{content.cg}</p>
                        </>
                      )}
                    </>
                  )}
                </React.Fragment>
              ))}

            <div className={styles.popupActions}>
              <p className={`${styles.popupInfoText} t3`}>
                {!info.object.redirectObject && (
                  <>
                    {objectsInRoom.find((obj) => !obj.found) &&
                      `Il vous reste d’autres objets à découvrir dans ${rooms[currentRoom]
                        ? rooms[currentRoom].title
                        : "cette pièce"
                      },  restez en alerte ;)`}
                    {objectsInRoom.every((obj) => obj.found) &&
                      activeScenario &&
                      activeScenario.objects.find((obj) => !obj.found) &&
                      `Vous avez trouvé tous les indices qui se cachaient ici, mais il vous reste des objets à découvrir dans ${rooms[
                        activeScenario.objects.find((obj) => !obj.found).room
                      ]
                        ? rooms[
                          activeScenario.objects.find((obj) => !obj.found)
                            .room
                        ].title
                        : "les autres pièces"
                      }...`}
                    {objectsInRoom.every((obj) => obj.found) &&
                      activeScenario &&
                      activeScenario.objects.every((obj) => obj.found) &&
                      "Il ne reste plus d’indice à découvrir ici mais le hall d’entrée en cache d’autres qui n’attendent que vous pour être trouvés..."}
                  </>
                )}
              </p>
              <div className={styles.scenarioCtas}>
                {info.object.redirectObject ? (
                  <>
                    {objectsInRoom.find((obj) => !obj.found) &&
                      <Button
                        preIcon={<ArrowBackIcon />}
                        onClick={() => animateOut()}
                      >
                        {`Poursuivre dans ${rooms[currentRoom].title}`}
                      </Button>
                    }
                    <Button
                      type={"secondary"}
                      postIcon={<ArrowForwardIcon />}
                      onClick={() => redirectTo(info.object.redirectObject)}
                    >
                      {`Aller dans ${rooms[info.object.redirectObject.room].title}`}
                    </Button>
                  </>
                ) : (
                  <>
                    {objectsInRoom.find((obj) => !obj.found) && (
                      <Button
                        preIcon={<ArrowBackIcon />}
                        onClick={() => animateOut()}
                      >
                        {`Poursuivre dans ${rooms[currentRoom].title}`}
                      </Button>
                    )}
                    {objectsInRoom.every((obj) => obj.found) &&
                      activeScenario &&
                      activeScenario.objects.find((obj) => !obj.found) && (
                        <Button
                          preIcon={<ArrowBackIcon />}
                          onClick={() =>
                            goToRoom(
                              activeScenario.objects.find((obj) => !obj.found)
                                .room,
                            )}
                        >
                          {`Aller dans ${rooms[
                            activeScenario.objects.find((obj) => !obj.found)
                              .room
                          ]
                            ? rooms[
                              activeScenario.objects.find(
                                (obj) => !obj.found,
                              ).room
                            ].title
                            : ""
                            }`}
                        </Button>
                      )}
                    {objectsInRoom.every((obj) => obj.found) &&
                      activeScenario &&
                      activeScenario.objects.every((obj) => obj.found) && (
                        <Button
                          postIcon={<ArrowForwardIcon />}
                          onClick={() => goToRoom("hall")}
                        >
                          Aller dans le hall
                        </Button>
                      )}
                  </>
                )}
              </div>
            </div>
          </>
        )}

        {info && info.type === "scenario" && (
          <>
            {(completedScenarios.includes(info.scenario.id)
            ) ? (
              <>
                <div className={styles.scenarioImage}>
                  <img
                    src={info.scenario.image}
                    alt={`Image de la quête : ${info.scenario.title}`}
                  />
                  <div className={styles.scenarioHead}>
                    <p className={`${styles.questLabel} t3`}>Quête</p>

                    <h5 className={styles.popupInfoTitle} aria-live={"polite"}>
                      {info.scenario.title}
                    </h5>
                  </div>
                </div>
                <div className={styles.separationLine}></div>

                <p className={styles.popupInfoTitle}>Félicitations !</p>

                <p className={`${styles.popupInfoText} t3`} aria-live={"polite"}>
                  🥳 🥳 🥳 : vous avez terminé la quête "{info.scenario.title}" !
                  <br />
                  <br />À vous de choisir la suite de votre parcours :
                  <br />- découvrir les contenus collectés dans votre panier,
                  <br />- cliquer sur d'autres objets dans ce hall pour lancer une
                  nouvelle quête.
                </p>
                <p className={`${styles.popupInfoText} t3`}>
                  Dans tous les cas, nous restons à vos côtés. Quels que soient vos
                  projets, nous sommes à votre écoute : contactez votre conseiller
                  Macif.
                </p>

                <button
                  className={`${styles.endCta} t3`}
                  onClick={handleClickRdv}
                  target="_blank"
                >
                  Je prends rendez-vous
                </button>
              </>
            ) : (<>  <div className={styles.scenarioImage}>
              <img
                src={info.scenario.image}
                alt={`Image de la quête : ${info.scenario.title}`}
              />
              <div className={styles.scenarioHead}>
                <p className={`${styles.questLabel} t3`}>Quête</p>

                <h5 className={styles.popupInfoTitle} aria-live={"polite"}>
                  {info.scenario.title}
                </h5>
              </div>

            </div>

              <p
                className={`${styles.popupInfoText} t3`}
                aria-live={"polite"}
                dangerouslySetInnerHTML={{ __html: info.scenario.text }}
              ></p>

              <div className={styles.scenarioCtas}>
                {info.scenario.roomLinks &&
                  info.scenario.roomLinks.map((link, i) => (
                    <Button
                      key={link.id || i}
                      preIcon={i !== 1 && <ArrowBackIcon />}
                      postIcon={i === 1 && <ArrowForwardIcon />}
                      onClick={() => goToRoom(link.id)}
                      type={i === 0 ? "primary" : "secondary"}
                    >
                      {link.label}
                    </Button>
                  ))}
              </div>
            </>
            )}

          </>
        )}




        {info && info.type === "finish" && (
          <>
            {(completedScenarios.length === 5) ? (<>
              <h5 className={styles.popupInfoTitle}>Félicitations !</h5>


              <p className={`${styles.popupInfoText} t3`} aria-live={"polite"}>
                🥳 🥳 🥳 : vous avez terminé toutes les quêtes !
                <br />
                N'hésitez pas à consulter les sujets que vous avez stockés dans votre panier.
              </p>


              <p className={`${styles.popupInfoText} t3`}>
                Dans tous les cas, nous restons à vos côtés. Quels que soient vos
                projets, nous sommes à votre écoute : contactez votre conseiller
                Macif.
              </p>

              <button
                className={`${styles.endCta} t3`}
                onClick={handleClickRdv}
                target="_blank"
              >
                Je prends rendez-vous
              </button>
            </>) : (<>
              <div className={styles.scenarioImage}>
                <img
                  src={info.scenario.image}
                  alt={`Image de la quête : ${info.scenario.title}`}
                />
                <div className={styles.scenarioHead}>
                  <p className={`${styles.questLabel} t3`}>Quête</p>

                  <h5 className={styles.popupInfoTitle} aria-live={"polite"}>
                    {info.scenario.title}
                  </h5>
                </div>
              </div>
              <div className={styles.separationLine}></div>
              <h5 className={styles.popupInfoTitle}>Félicitations !</h5>


              <p className={`${styles.popupInfoText} t3`} aria-live={"polite"}>
                🥳 🥳 🥳 : vous avez terminé la quête "{info.scenario.title}" !
                <br />
                <br />À vous de choisir la suite de votre parcours :
                <br />- découvrir les contenus collectés dans votre panier,
                <br />- cliquer sur d'autres objets dans ce hall pour lancer une
                nouvelle quête.
              </p>
              <p className={`${styles.popupInfoText} t3`}>
                Dans tous les cas, nous restons à vos côtés. Quels que soient vos
                projets, nous sommes à votre écoute : contactez votre conseiller
                Macif.
              </p>

              <button
                className={`${styles.endCta} t3`}
                onClick={handleClickRdv}
                target="_blank"
              >
                Je prends rendez-vous
              </button>
            </>)}
          </>
        )}
      </div>
    </>
  );
}

export default PopupInfo;
